import { ActivityListItemDto } from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { cn, useLocaleStore } from '@/lib/core';
import { formatActivityDate } from '@/lib/core/utils/dateTime';
import { getActivityStatusColor } from '@/lib/core/utils/activityStatus';

interface ActivityListPastProps {
  items: ActivityListItemDto[];
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMoreLabel?: string;
  onLoadMore?: () => void;
  onItemClick?: (id: string) => void;
}

export function ActivityListPast({
  items,
  hasMore,
  loadingMore,
  loadMoreLabel = 'Load more',
  onLoadMore,
  onItemClick,
}: ActivityListPastProps) {
  const locale = useLocaleStore(state => state.locale);
  return (
    <div className="flex flex-col gap-4 pb-[calc(((env(safe-area-inset-bottom)+1.5rem)*2)+48px)]">
      <div className="flex flex-col">
        {items.map(item => {
          const chips = item.summaryLines;

          return (
            <div
              key={item.id}
              className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] py-4"
            >
              {/* row 1: date + optional status | category */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase">
                    {formatActivityDate(item.date, locale)}
                  </span>
                  {item.status && (
                    <>
                      <span className="text-jncquoi-neutral-500 text-[4px]">●</span>
                      <span
                        className={cn(
                          'font-pp-neue-montreal shrink-0 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase',
                          getActivityStatusColor(item.status)
                        )}
                      >
                        {item.status}
                      </span>
                    </>
                  )}
                </div>
                {item.category && (
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase">
                    {item.category}
                  </span>
                )}
              </div>
              {/* row 2: title + arrow */}
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 text-left"
                onClick={() => item.id && onItemClick?.(item.id)}
              >
                <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 truncate text-[14px] font-semibold tracking-[0.5px]">
                  {item.title}
                </span>
                <Icon
                  name="nav-arrow-right"
                  width={20}
                  height={20}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
              </button>
              {/* row 3: summary chips */}
              {(chips?.length ?? 0) > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {chips?.map((chip, i) => (
                    <span key={i} className="flex items-center gap-2">
                      {i > 0 && <span className="text-[8px] text-[#bababa]">●</span>}
                      <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] whitespace-nowrap">
                        {chip}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {hasMore && (
        <div className="flex justify-center pt-4">
          {loadingMore ? (
            <SpinLoading wrapperClassName="min-h-[40px]" />
          ) : (
            <Button variant="secondary-outline" palette="dark" size="sm" onClick={onLoadMore}>
              {loadMoreLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
